import type { ReactNode } from "react";

import { Box } from "@/design-system/Box";
import { Card } from "@/design-system/Card";
import { Stack } from "@/design-system/Stack";
import { RuntimeBackgroundImage } from "@/ui-adapters/RuntimeBackgroundImage";

type ServiceDetailsBlockCardProps = {
  title: string;
  subtitle?: string;
  children: ReactNode;
  renderBadges?: () => ReactNode;
} & (
  | {
      backgroundImage: string;
      thumbnailImage: string | undefined;
    }
  | {
      backgroundImage?: never;
      thumbnailImage?: never;
    }
);

export function ServiceDetailsBlockCard({
  title,
  subtitle,
  backgroundImage,
  thumbnailImage,
  children,
  renderBadges,
}: ServiceDetailsBlockCardProps): ReactNode {
  return (
    <Card as="article" density="flush">
      {backgroundImage && (
        <header className="relative w-full">
          <RuntimeBackgroundImage imageUrl={backgroundImage} presentation="cardHeader">
            {renderBadges && <aside className="absolute top-3 left-3 flex gap-space-sm">{renderBadges()}</aside>}
          </RuntimeBackgroundImage>
          {thumbnailImage && (
            <img
              className="absolute right-8 size-thumbnail-compact -translate-y-1/2 rounded-full border-4 border-background-default bg-background-default object-cover"
              src={thumbnailImage}
              alt={title}
            />
          )}
        </header>
      )}
      <Box padding="md">
        <Stack gap="sm">
          {title && <span className="text-heading-3 wrap-break-word">{title}</span>}
          {subtitle && <span className="text-body-emphasis wrap-break-word">{subtitle}</span>}
          {children}
        </Stack>
      </Box>
    </Card>
  );
}
